import { Checkbox, Form, Input } from "antd";
import React, { Dispatch, SetStateAction } from "react";
import intl from "react-intl-universal";
import { useDispatch } from "react-redux";
import TextButton from "src/components/AuthenticationPage/TextButton";
import Spacer from "src/components/base/Spacer";
import { requestSignup } from "src/redux/modules/Signup";
import useSelector from "src/redux/store/useSelector";
import { dataType } from "src/types/authentication.type";
import { authTitle, AUTH_TITLE } from "src/utils";
import { eRequirement, pRequirement } from "src/utils/regCheck";
import useGoogleAnalytics, { Category } from "src/utils/GoogleAnalytics";
import { Flex } from "src/components";
import styled from "styled-components";
import GoolgeLoginButton, {
    StyledButton,
} from "src/components/AuthenticationPage/GoogleLoginButton";
import OrDivider from "src/components/AuthenticationPage/OrDivider";
import { TERMS_URL } from "src/utils/uri";
import { syncAutofillOnSubmit } from "src/components/AuthenticationPage/syncAutofill";

type SignupProps = {
    setTitle: React.Dispatch<React.SetStateAction<string>>;
    setData: Dispatch<SetStateAction<dataType>>;
    data: dataType;
    onAuthAttempt: () => void;
};

const SignUp: React.FC<SignupProps> = ({ data, setData, setTitle, onAuthAttempt }) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const loading = useSelector((state) => state.login?.loading || state.signup?.loading);
    const { sendEvent } = useGoogleAnalytics();

    const handleLogin = () => {
        setTitle(authTitle(AUTH_TITLE.LOGIN));
    };

    const handleSignupForm = (values: { email: string; password: string }) => {
        sendEvent(Category.EDIT, `user interacts with signup form`, "SignUp");
        const email = values.email.trim().toLowerCase();
        const body = { email, password: values.password };
        onAuthAttempt();
        setData({
            ...data,
            email,
            password: values.password,
        });
        dispatch(requestSignup(body));
    };

    return (
        <Flex
            column
            align="center"
            justify="center"
            style={{ width: "100%" }}
            onSubmitCapture={syncAutofillOnSubmit(form)}
        >
            <Spacer height={16} />
            <Form
                layout={"vertical"}
                form={form}
                style={{ width: "100%" }}
                onFinish={handleSignupForm}
            >
                <Form.Item
                    name="email"
                    initialValue={data.email}
                    rules={[
                        {
                            required: true,
                            message: intl.get("authentication.warning.inputEmail"),
                        },
                        {
                            pattern: eRequirement,
                            message: intl.get("authentication.warning.inputValidEmail"),
                        },
                    ]}
                >
                    <Input
                        size="large"
                        autoComplete="username"
                        placeholder={intl.get("authentication.shared.email")}
                    />
                </Form.Item>
                <Form.Item
                    name="password"
                    rules={[
                        {
                            required: true,
                            message: intl.get("authentication.warning.inputPW"),
                        },
                        {
                            pattern: pRequirement,
                            message: intl.get("authentication.warning.inputValidPW"),
                        },
                    ]}
                >
                    <Input.Password
                        size="large"
                        autoComplete="new-password"
                        placeholder={intl.get("authentication.shared.pw")}
                    />
                </Form.Item>
                <Form.Item
                    name="rpassword"
                    dependencies={["password"]}
                    rules={[
                        { required: true, message: intl.get("authentication.warning.retypePW") },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue("password") === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(
                                    new Error(intl.get("authentication.warning.pwNotMatch")),
                                );
                            },
                        }),
                    ]}
                >
                    <Input.Password
                        size="large"
                        autoComplete="new-password"
                        placeholder={intl.get("authentication.shared.retypePW")}
                    />
                </Form.Item>

                <Form.Item
                    name="agreement"
                    valuePropName="checked"
                    rules={[
                        {
                            validator: (_, value) =>
                                value
                                    ? Promise.resolve()
                                    : Promise.reject(
                                          new Error(intl.get("authentication.warning.acceptTerm")),
                                      ),
                        },
                    ]}
                >
                    <StyledCheckbox>
                        {intl.get("authentication.signup.confirmTerms")}{" "}
                        <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
                            {intl.get("authentication.signup.termOfUse")}
                        </a>
                    </StyledCheckbox>
                </Form.Item>

                <Form.Item>
                    <StyledButton
                        loading={loading}
                        htmlType="submit"
                        block
                        type="primary"
                        size="large"
                        disabled={loading}
                    >
                        {intl.get("authentication.shared.continueWithEmail")}
                    </StyledButton>
                </Form.Item>
            </Form>
            <OrDivider />
            <GoolgeLoginButton renderType="register" />
            <Spacer height={16} />
            <TextButton
                text={intl.get("authentication.signup.alreadyHave")}
                button={intl.get("authentication.shared.login")}
                handlePress={handleLogin}
            />
        </Flex>
    );
};

const StyledCheckbox = styled(Checkbox)`
    .ant-checkbox-inner {
        border: ${({ theme }) => `1px solid ${theme.color.greySecondaryM}`};
        border-radius: 2px;
    }
`;

export default SignUp;
